// Cross-platform feedback helper for mobile interactions.
//
// Why this exists: the Web Vibration API is fully supported on Android
// (Chrome / Firefox / Samsung Internet) but NOT on iOS Safari at all
// (one narrow exception in iOS 17.4+ for the hidden <input switch>
// toggle, unusable for our case). If we drove user feedback from
// `navigator.vibrate` alone, iPhone users would feel nothing on every
// HP change, every craft, every save — silent rejection.
//
// So feedback() runs three modalities in parallel where each is
// available: vibration (Android), optional audio (platform-agnostic),
// and visual is left to the caller's CSS :active state on the
// triggering element. Visual is the *primary* signal; the others are
// additive. Callers should always have a CSS press state too.
//
// Audio is currently stubbed — A.4 polish lands the actual cue
// playback (small generated tones via Web Audio API, no asset files
// needed). The interface stays stable so callers can light up audio
// without revisiting their call sites.

const PATTERNS = {
  // Short tap acknowledgement — for + / − / equip / consume confirms.
  tap: 10,
  // Long-press ramp-up tick — fires repeatedly during a held button.
  'longpress-tick': 5,
  // Damage taken — single firm pulse.
  damage: 20,
  // Healing — light double pulse.
  heal: [10, 30, 10],
  // Successful craft / level up / save passed — three-pulse success.
  success: [15, 40, 15, 40, 30],
  // Failed action / validation error — warning pattern.
  warning: [50, 30, 50],
  // Critical alert — heavier success pattern.
  critical: [25, 50, 25, 50, 50],
};

// Feature flags. A.4 wires these to character settings; for now they're
// module-local so callers can light up feedback safely without a settings
// dependency. Calling feedback() on a desktop is a no-op (no Vibration
// support on most desktop browsers either, and even where it is, we
// don't want surprise vibrations on a laptop).
let hapticEnabled = true;
let audioEnabled = false;

export const setHapticEnabled = (v) => { hapticEnabled = !!v; };
export const setAudioEnabled = (v) => { audioEnabled = !!v; };
export const isHapticSupported = () => typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

// Trigger feedback for a given semantic type. Safe to call on any
// platform — falls through to no-op when the modality isn't available.
//
// Visual feedback (button press states) is NOT routed through here —
// that's CSS :active on the element. This is for the non-visual layer.
export const feedback = (type) => {
  const pattern = PATTERNS[type];
  if (pattern === undefined) return;

  // Vibration: Android Chrome/Firefox/Samsung honor this. iOS Safari
  // returns true silently — the call no-ops without throwing, so we
  // don't need a UA sniff.
  if (hapticEnabled && isHapticSupported()) {
    try {
      navigator.vibrate(pattern);
    } catch {
      // iOS variants and some embedded browsers throw on vibrate; ignore.
    }
  }

  // Audio: stubbed until A.4. When enabled, we'll synthesize a short
  // tone matching the pattern shape (single beep for tap, descending
  // pair for damage, etc.) via Web Audio API. No asset files required.
  if (audioEnabled) {
    // TODO(A.4): Web Audio API tone generation keyed off `type`.
  }
};

// Convenience: trigger a tap-down feedback at touchstart so the user
// feels the press registered before the action commits at touchend.
// Pairs with the CSS :active visual state for the <100ms feedback rule
// from Apple HIG. Use sparingly — only on interactions that genuinely
// benefit (HP +/-, equip toggle, craft button), not on every nav tap.
export const tapDown = () => feedback('tap');
